// Translated UI wrapper for AWARD_NAMES from common/constants.
// Worker imports AWARD_NAMES directly from common/constants; UI imports this.
// Uses a Proxy so property access translates lazily (i18n must be initialized
// before access, hence not at module load time — see feedback-i18n-lazy).
import { AWARD_NAMES as AWARD_NAMES_BASE } from "../../../../common/constants.ts";
import i18n from "./i18n.ts";

type AwardKey = keyof typeof AWARD_NAMES_BASE;

export const AWARD_NAMES = new Proxy(AWARD_NAMES_BASE, {
	get(target, key) {
		const base = target[key as AwardKey];
		if (typeof base !== "string") {
			return base;
		}
		if (!i18n.isInitialized) {
			return base;
		}
		const translated = i18n.t(`award.${String(key)}`, {
			ns: "ui",
			defaultValue: base,
		});
		return typeof translated === "string" && translated.length > 0
			? translated
			: base;
	},
}) as typeof AWARD_NAMES_BASE;

// Reverse lookup: worker views sometimes emit full English award names like
// "Most Valuable Player" instead of the short key "mvp". This builds a reverse
// map at access time so UI can still translate them to localized names.
let reverseMapCache: Map<string, AwardKey> | null = null;
const getReverseMap = (): Map<string, AwardKey> => {
	if (reverseMapCache) return reverseMapCache;
	const map = new Map<string, AwardKey>();
	for (const k of Object.keys(AWARD_NAMES_BASE) as AwardKey[]) {
		const v = AWARD_NAMES_BASE[k];
		if (typeof v === "string") map.set(v, k);
	}
	reverseMapCache = map;
	return map;
};

export const translateAwardName = (englishName: string): string => {
	if (!i18n.isInitialized) return englishName;
	const key = getReverseMap().get(englishName);
	if (!key) return englishName;
	const translated = i18n.t(`award.${String(key)}`, {
		ns: "ui",
		defaultValue: englishName,
	});
	return typeof translated === "string" && translated.length > 0
		? translated
		: englishName;
};
